import {
  Box, Button, Card, CardContent, Chip, Stack, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import RuleIcon from '@mui/icons-material/Rule';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Link as RouterLink } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { patient } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import type { MedicalConsent } from '@/types/medical';

/**
 * Consents page.
 *
 * The patient sees:
 *   - active consents (granted, not revoked)
 *   - revoked consents (with timestamp)
 *
 * Each row exposes a "Revoke" action that opens a ConfirmDialog requiring
 * a reason. Revoking writes a separate revocation receipt — the data
 * trail is monotonic.
 *
 * The "Receipts" link routes to the wallet page where the patient can
 * cryptographically verify each receipt.
 */

export function PatientConsentsPage() {
  const q = useApiQuery({
    queryKey: ['patient', 'consents'],
    queryFn:  patient.consents.list,
  });

  const [revokeTarget, setRevokeTarget] = useState<MedicalConsent | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const revokeMut = useApiMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      patient.consents.revoke(id, reason),
    onSuccess: () => {
      toast.success('Consent revoked.');
      qc.invalidateQueries({ queryKey: ['patient', 'consents'] });
      setRevokeTarget(null);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Consents & permissions"
        subtitle="Every consent is signed and recorded. You can revoke at any time — your trail stays."
        actions={
          <Button
            component={RouterLink}
            to="/patient/wallet"
            variant="outlined"
            startIcon={<VerifiedIcon />}
          >
            View receipts
          </Button>
        }
      />

      <DataState
        query={q}
        empty={{
          title: 'No consents on record',
          description: 'When you give consent during a booking, it appears here.',
        }}
      >
        {(items) => {
          const active = items.filter((c) => !c.revoked_at);
          const revoked = items.filter((c) => Boolean(c.revoked_at));
          return (
            <>
              {active.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="overline" color="text.secondary"
                              sx={{ display: 'block', mb: 1 }}>
                    Active ({active.length})
                  </Typography>
                  <Stack spacing={1.5}>
                    {active.map((c) => (
                      <ConsentRow
                        key={c.id} consent={c}
                        onRevoke={() => setRevokeTarget(c)}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              {revoked.length > 0 && (
                <Box>
                  <Typography variant="overline" color="text.secondary"
                              sx={{ display: 'block', mb: 1 }}>
                    Revoked ({revoked.length})
                  </Typography>
                  <Stack spacing={1.5}>
                    {revoked.map((c) => <ConsentRow key={c.id} consent={c} />)}
                  </Stack>
                </Box>
              )}
            </>
          );
        }}
      </DataState>

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        title="Revoke this consent?"
        description={
          revokeTarget
            ? `You are revoking your consent to "${revokeTarget.consent_type.replace(/_/g, ' ')}". A revocation receipt will be created.`
            : ''
        }
        destructive
        reasonRequired
        confirmLabel="Revoke"
        onCancel={() => setRevokeTarget(null)}
        onConfirm={(reason) =>
          revokeTarget
            ? revokeMut.mutateAsync({ id: revokeTarget.id, reason: reason ?? '' })
            : Promise.resolve()
        }
      />
    </>
  );
}

function ConsentRow({
  consent, onRevoke,
}: {
  consent: MedicalConsent;
  onRevoke?: () => void;
}) {
  const isRevoked = Boolean(consent.revoked_at);
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle',
            color: isRevoked ? 'text.disabled' : 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <RuleIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                noWrap
                sx={{
                  textTransform: 'capitalize',
                  textDecoration: isRevoked ? 'line-through' : 'none',
                  color: isRevoked ? 'text.disabled' : 'text.primary',
                }}
              >
                {consent.consent_type.replace(/_/g, ' ')}
              </Typography>
              {!isRevoked && consent.granted && (
                <Chip size="small" label="granted" color="success" variant="outlined" />
              )}
              {isRevoked && (
                <Chip size="small" label="revoked" color="default" variant="outlined" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" className="tabular">
              granted {formatDateTime(consent.granted_at)}
              {consent.revoked_at && (
                <> · revoked {formatDateTime(consent.revoked_at)}</>
              )}
            </Typography>
            {consent.revocation_reason && (
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}>
                Reason: {consent.revocation_reason}
              </Typography>
            )}
          </Box>
          {!isRevoked && onRevoke && (
            <Button color="error" size="small" onClick={onRevoke}>
              Revoke
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
